<script setup lang="ts">
import { CalendarDays, Lock } from 'lucide-vue-next'
import {breakpointsTailwind, useBreakpoints} from "@vueuse/core";
const breakpoints = useBreakpoints(breakpointsTailwind)



interface SlotColor {
  bg: string
  text: string
  'text-secondary': string
  'text-tertiary': string
}

interface TimeSlot {
  day: string
  time: string
  available: boolean
  booked?: boolean
  type?: string | null
  subject_name?: string | null
  level_name?: string | null
  color?: SlotColor | null
  tooltip?: string
}

interface LegendItem {
  label: string
  bg: string
}

interface Props {
  tutorName: string
  availability: TimeSlot[][]
  legend?: LegendItem[]
}

const props = defineProps<Props>()

const days = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN']

const getSubjectName = (subjectName: string) => {
    return breakpoints.greaterOrEqual('md').value
        ? subjectName
        : subjectName.substring(0,4);
}

// Get background class for a slot
const getSlotBgClass = (slot: TimeSlot): string => {
  if (!slot.available) return 'bg-gray-100'
  if (slot.booked) return 'bg-amber-100'
  if (slot.type === 'group' && slot.color) return slot.color.bg
  if (slot.type === 'group') return 'bg-purple-100'
  return 'bg-green-100'
}

// Get text class for primary text (subject name)
const getSlotTextClass = (slot: TimeSlot): string => {
  if (slot.type === 'group' && slot.color) return slot.color.text
  return 'text-purple-700'
}

// Get text class for secondary text (level name)
const getSlotSecondaryTextClass = (slot: TimeSlot): string => {
  if (slot.type === 'group' && slot.color) return slot.color['text-secondary']
  return 'text-purple-600'
}

// Get text class for tertiary text (time)
const getSlotTertiaryTextClass = (slot: TimeSlot): string => {
  if (slot.type === 'group' && slot.color) return slot.color['text-tertiary']
  return 'text-purple-500'
}
</script>

<template>
  <section class="md:rounded-2xl md:bg-surface md:p-6 md:shadow-sm">
    <div class="mb-4 text-center">
      <div class="mb-3 inline-flex size-12 items-center justify-center rounded-xl bg-gradient-to-br from-teal-500 to-teal-800">
        <CalendarDays class="size-6 text-white" />
      </div>
      <h3 class="mb-1 text-lg font-bold text-ink">Weekly Availability</h3>
      <p class="text-xs text-muted">{{ tutorName ? `${tutorName}'s teaching schedule` : 'Teaching schedule' }}</p>
    </div>

    <!--
      A table with the time in its own column.

      It was a seven-column grid in which every cell repeated its own start time,
      so the same seven values were printed forty-odd times and each cell had
      three lines of text in about fifty pixels. The hour belongs to the row, not
      to each cell, which frees the cell to say only what is happening.

      Fluid rather than a fixed minimum width: the profile column has no
      `min-w-0`, so a minimum here would stretch the whole page sideways on a
      phone instead of scrolling within the card.
    -->
    <table class="w-full table-fixed border-collapse">
      <caption class="sr-only">Weekly teaching availability by day and time</caption>
      <thead>
        <tr>
          <th scope="col" class="w-10 pb-2 text-left text-[10px] font-semibold text-muted sm:w-14">Time</th>
          <th
            v-for="day in days"
            :key="day"
            scope="col"
            class="pb-2 text-center text-[10px] font-semibold text-muted"
          >{{ day }}</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="(row, rowIndex) in availability" :key="rowIndex">
          <th scope="row" class="pr-1 text-left align-middle text-[10px] font-semibold text-muted whitespace-nowrap">
            {{ row[0]?.time }}
          </th>
          <td v-for="(slot, slotIndex) in row" :key="slotIndex" class="p-0.5 align-top">
            <div
              :title="slot.tooltip"
              :class="[
                'flex min-h-[44px] flex-col items-center justify-center rounded px-0.5 py-1.5 text-center',
                getSlotBgClass(slot),
              ]"
            >
              <template v-if="!slot.available">
                <Lock class="size-3 text-subtle" />
                <span class="sr-only">Not available</span>
              </template>

              <template v-else-if="slot.booked">
                <span class="text-[9px] font-medium text-amber-700 line-through">Booked</span>
              </template>

              <template v-else-if="slot.type === 'group'">
                <p :class="['text-[9px] font-semibold leading-tight', getSlotTextClass(slot)]">
                  {{ getSubjectName(slot.subject_name) || 'Group' }}
                </p>
                <p :class="['truncate text-[9px] leading-tight', getSlotSecondaryTextClass(slot)]">
                  {{ slot.level_name || '' }}
                </p>
              </template>

              <template v-else>
                <span class="text-[9px] font-semibold leading-tight text-green-700">1:1</span>
                <span class="truncate text-[8px] leading-tight text-green-600">Available</span>
              </template>
            </div>
          </td>
        </tr>
      </tbody>
    </table>

    <div class="mt-4 space-y-1.5">
      <div class="flex items-center gap-2">
        <div class="size-3 rounded bg-green-100" />
        <span class="text-xs text-muted">1:1 Session</span>
      </div>

      <template v-if="legend && legend.length > 0">
        <div v-for="(item, index) in legend" :key="index" class="flex items-center gap-2">
          <div :class="['size-3 rounded', item.bg]" />
          <span class="text-xs text-muted">{{ item.label }}</span>
        </div>
      </template>

      <div v-else class="flex items-center gap-2">
        <div class="size-3 rounded bg-purple-100" />
        <span class="text-xs text-muted">Group Session</span>
      </div>

      <div class="flex items-center gap-2">
        <div class="size-3 rounded bg-amber-100" />
        <span class="text-xs text-muted line-through">Booked</span>
      </div>
      <div class="flex items-center gap-2">
        <div class="size-3 rounded bg-gray-100" />
        <span class="text-xs text-muted">Not Available</span>
      </div>
    </div>
  </section>
</template>
